/* eslint-disable max-lines -- словарь текстов шапки редактора, ru/en рядом: делить по языкам нельзя */
import { defineMessages } from '@/i18n'
import type { Lang } from '@/types/lang'
import { LANG_LABEL } from '../../lib/contentLangLabels'

/** Название языка в английском интерфейсе (`LANG_LABEL` — самоназвания: «Русский», «English»). */
export const EN_LANG_NAME: Record<Lang, string> = { ru: 'Russian', en: 'English' }

export const messages = defineMessages({
  ru: {
    a11yCourseTitle: 'Название курса',
    soon: 'скоро',
    go: 'Перейти',
    soundOff: 'Выключить звук',
    soundOn: 'Включить звук',
    drawImages: 'Нарисовать картинки',
    noRights: 'Нет прав',
    deleteLangTitle: (lang: Lang | null) => `Удалить ${lang ? `${LANG_LABEL[lang]} язык` : ''}?`,
    deleteLangBody:
      'Все переводы уроков и блоков на этом языке будут удалены без возможности восстановления.',
    cancel: 'Отмена',
    delete: 'Удалить',
    editLang: (lang: Lang) => `Язык правки — ${LANG_LABEL[lang]}`,
    issuesHeading: 'Чтобы опубликовать, заполните:',
    warningsHeading: 'Рекомендуем поправить:',
    colCourse: 'Курс',
    colLang: 'Язык',
    colLesson: 'Урок',
    colField: 'Поле',
    colBlock: 'Блок',
    colProblem: 'Что не так',
    goToField: (field: string, lang: string) => `Перейти к полю «${field}» ${lang}`,
    goToLesson: (lesson: string | number) => `Перейти к уроку ${lesson}`,
    goToBlock: (lesson: string | number, screen: string | number) =>
      `Перейти к уроку ${lesson}, блоку ${screen}`,
    checkingLessons: 'Проверяю уроки…',
    madeBase: (lang: Lang, source: Lang) =>
      `${LANG_LABEL[lang]} язык стал базовым. ${LANG_LABEL[source]} язык остаётся языком перевода.`,
    makeBaseFailed: 'Не удалось сменить базовый язык',
    langDeleted: (lang: Lang) => `${LANG_LABEL[lang]} язык удалён`,
    deleteLangFailed: 'Не удалось удалить язык',
    addLangFailed: 'Не удалось добавить язык',
    baseBadge: 'Базовый',
    whatIsBase: 'Что такое базовый язык',
    makeBase: 'сделать базовым',
    makeBaseNeedsVisible: 'Сначала включите видимость языка',
    actionsDuringRun: 'Недоступно, пока идёт генерация или перевод',
    langSwitch: (lang: Lang) => `Язык ${LANG_LABEL[lang]}`,
    langVisible: (lang: Lang) => `Показывать ученикам: ${LANG_LABEL[lang]}`,
    translationMenu: 'Перевод',
    translationMenuRunning: 'Идёт перевод…',
    langShown: 'Виден ученикам',
    langActions: (lang: Lang) => `Действия: ${LANG_LABEL[lang]}`,
    makeBaseItem: 'Сделать базовым',
    deleteLangItem: 'Удалить язык',
    langHidden: 'Скрыт от учеников',
    titleTranslating: 'Идёт перевод курса',
    titleSaveFailed: 'Не удалось сохранить название',
    untitled: 'Без названия',
    undo: 'Отменить правку блока',
    redo: 'Вернуть правку блока',
    publish: 'Опубликовать',
    statusUnknown: 'Статус неизвестен',
    pending: 'На проверке',
    reviewingUpdate: 'Проверяется…',
    rejected: 'Отклонено',
    failed: 'Не проверено',
    live: 'Опубликовано',
    update: 'Обновить',
    updateRejected: 'Обновление отклонено',
    loadingStatus: 'Загружаю статус публикации…',
    waitSave: 'Дождитесь сохранения',
    saveFailed: 'Не удалось сохранить изменения',
    preview: 'Предпросмотр',
    previewHint: 'Пройти курс как ученик',
    previewDisabled: 'Выберите урок с блоками',
  },
  en: {
    a11yCourseTitle: 'Course title',
    soon: 'Soon',
    go: 'Go',
    soundOff: 'Mute sound',
    soundOn: 'Unmute sound',
    drawImages: 'Generate images',
    noRights: 'No permission',
    deleteLangTitle: (lang: Lang | null) => `Delete ${lang ? EN_LANG_NAME[lang] : 'language'}?`,
    deleteLangBody:
      'All lesson and block translations in this language will be permanently deleted.',
    cancel: 'Cancel',
    delete: 'Delete',
    editLang: (lang: Lang) => `Editing language — ${EN_LANG_NAME[lang]}`,
    issuesHeading: 'To publish, fill in:',
    warningsHeading: 'We recommend fixing:',
    colCourse: 'Course',
    colLang: 'Language',
    colLesson: 'Lesson',
    colField: 'Field',
    colBlock: 'Block',
    colProblem: 'Issue',
    goToField: (field: string, lang: string) => `Go to the “${field}” field ${lang}`,
    goToLesson: (lesson: string | number) => `Go to lesson ${lesson}`,
    goToBlock: (lesson: string | number, screen: string | number) =>
      `Go to lesson ${lesson}, block ${screen}`,
    checkingLessons: 'Checking lessons…',
    madeBase: (lang: Lang, source: Lang) =>
      `${EN_LANG_NAME[lang]} is now the source language. ${EN_LANG_NAME[source]} stays as a translation.`,
    makeBaseFailed: 'Couldn’t change the source language',
    langDeleted: (lang: Lang) => `${EN_LANG_NAME[lang]} deleted`,
    deleteLangFailed: 'Couldn’t delete the language',
    addLangFailed: 'Couldn’t add the language',
    baseBadge: 'Source',
    whatIsBase: 'What is the source language?',
    makeBase: 'Set as source',
    makeBaseNeedsVisible: 'Turn on language visibility first',
    actionsDuringRun: 'Unavailable while generation or translation is running',
    langSwitch: (lang: Lang) => `${EN_LANG_NAME[lang]} language`,
    langVisible: (lang: Lang) => `Show ${EN_LANG_NAME[lang]} to learners`,
    translationMenu: 'Translation',
    translationMenuRunning: 'Translating…',
    langShown: 'Visible to learners',
    langActions: (lang: Lang) => `${EN_LANG_NAME[lang]} actions`,
    makeBaseItem: 'Set as source',
    deleteLangItem: 'Delete language',
    langHidden: 'Hidden from learners',
    titleTranslating: 'Course translation in progress',
    titleSaveFailed: 'Couldn’t save the title',
    untitled: 'Untitled',
    undo: 'Undo block edit',
    redo: 'Redo block edit',
    publish: 'Publish',
    statusUnknown: 'Status unknown',
    pending: 'Under review',
    reviewingUpdate: 'Reviewing…',
    rejected: 'Rejected',
    failed: 'Review error',
    live: 'Published',
    update: 'Update',
    updateRejected: 'Update rejected',
    loadingStatus: 'Loading publication status…',
    waitSave: 'Wait for changes to save',
    saveFailed: 'Couldn’t save changes',
    preview: 'Preview',
    previewHint: 'Take the course as a learner',
    previewDisabled: 'Select a lesson with blocks',
  },
})
